/**
 * Pure preset math for the V2 stats date-range control. All dates are
 * day-granularity local-midnight Dates (both bounds INCLUSIVE — the stats
 * API treats a day-granularity `to` as an inclusive last day).
 *
 * The anchor ("today") differs per mode: business mode anchors on the
 * branch's current business date (from GET /stats/context), calendar mode
 * on the browser's local day — same rationale as history-date-presets.ts.
 */
import {
  parseLocalDayInput,
  toDateInputValue,
} from '@/domain/pos/history-date-presets';

export type TStatsRangePreset =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

export interface IStatsDateRange {
  preset: TStatsRangePreset;
  /** Inclusive first day (local midnight). */
  from: Date;
  /** Inclusive last day (local midnight). */
  to: Date;
  /** true = legacy calendar semantics (`?calendar=1`). */
  calendar: boolean;
}

export const STATS_RANGE_PRESETS: Exclude<TStatsRangePreset, 'custom'>[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  'thisMonth',
  'lastMonth',
];

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Resolves a non-custom preset against an anchor day (local midnight). */
export function getStatsPresetRange(
  preset: Exclude<TStatsRangePreset, 'custom'>,
  anchor: Date
): { from: Date; to: Date } {
  switch (preset) {
    case 'today':
      return { from: new Date(anchor), to: new Date(anchor) };
    case 'yesterday': {
      const day = addDays(anchor, -1);
      return { from: day, to: new Date(day) };
    }
    case '7d':
      return { from: addDays(anchor, -6), to: new Date(anchor) };
    case '30d':
      return { from: addDays(anchor, -29), to: new Date(anchor) };
    case 'thisMonth':
      return {
        from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
        to: new Date(anchor),
      };
    case 'lastMonth':
      return {
        from: new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1),
        // Day 0 of the anchor month = last day of the previous month.
        to: new Date(anchor.getFullYear(), anchor.getMonth(), 0),
      };
  }
}

/**
 * Custom range from two date-input values; swapped bounds are normalized.
 * Returns null when either input is missing/invalid.
 */
export function getStatsCustomRange(
  fromInput: string,
  toInput: string
): { from: Date; to: Date } | null {
  const from = parseLocalDayInput(fromInput);
  const to = parseLocalDayInput(toInput);
  if (!from || !to) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

/** Serializable shape persisted in localStorage per control instance. */
export interface IStatsRangePersisted {
  preset: TStatsRangePreset;
  calendar: boolean;
  /** Only meaningful when preset === 'custom' ('yyyy-MM-dd'). */
  customFrom?: string;
  customTo?: string;
}

const FALLBACK_PRESET: Exclude<TStatsRangePreset, 'custom'> = '30d';

export const DEFAULT_STATS_RANGE_PERSISTED: IStatsRangePersisted = {
  preset: FALLBACK_PRESET,
  calendar: false,
};

function isKnownPreset(
  value: unknown
): value is Exclude<TStatsRangePreset, 'custom'> {
  return (STATS_RANGE_PRESETS as string[]).includes(String(value));
}

/**
 * Materializes a persisted state into a concrete range. localStorage is a
 * system boundary: unknown presets (renames, tampering) and invalid custom
 * bounds fall back to the default preset instead of crashing the control.
 */
export function resolveStatsRange(
  persisted: IStatsRangePersisted,
  anchor: Date
): IStatsDateRange {
  if (persisted.preset === 'custom') {
    const custom = getStatsCustomRange(
      persisted.customFrom ?? '',
      persisted.customTo ?? ''
    );
    if (custom) {
      return { preset: 'custom', calendar: !!persisted.calendar, ...custom };
    }
    return {
      preset: FALLBACK_PRESET,
      calendar: !!persisted.calendar,
      ...getStatsPresetRange(FALLBACK_PRESET, anchor),
    };
  }
  const preset = isKnownPreset(persisted.preset)
    ? persisted.preset
    : FALLBACK_PRESET;
  return {
    preset,
    calendar: !!persisted.calendar,
    ...getStatsPresetRange(preset, anchor),
  };
}

export { parseLocalDayInput, toDateInputValue };
